import { Link, useRouterState } from "@tanstack/react-router";
import { BookOpen, Boxes, PanelLeft, Plus, Settings, TrendingUp } from "lucide-react";
import { useSession } from "@/lib/auth-client";
import logoWhite from "../../../design-system/assets/logos/nymbl-logo-white.png";

/**
 * The audit app's primary navigation.
 *
 * Grouped the way the work is: **Audit** (the solutions you audit, and starting
 * a new one) above **Standard** (the Guide that does the judging, and the
 * reporting built from it). The Guide and Reporting carry phase badges because
 * both are later-phase scope and the badge is what stops them reading as
 * missing features.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof Boxes;
  /** Routes that should also light this item — a scorecard is still "Solutions". */
  alsoMatches?: string[];
  phase?: string;
}

const AUDIT_NAV: NavItem[] = [
  {
    to: "/solutions",
    label: "Solutions",
    icon: Boxes,
    // A run, scorecard or flag list is reached THROUGH a solution, so the rail
    // stays on Solutions rather than going dark on the deeper screens.
    alsoMatches: ["/runs"],
  },
  { to: "/new-audit", label: "New audit", icon: Plus },
];

const STANDARD_NAV: NavItem[] = [
  { to: "/guide", label: "Plan Guide", icon: BookOpen, phase: "P2" },
  { to: "/reporting", label: "Reporting", icon: TrendingUp, phase: "P3" },
];

function initials(name: string | null | undefined, email: string | null | undefined): string {
  const source = name?.trim() || email?.split("@")[0] || "?";
  const parts = source.split(/[\s._-]+/).filter((p) => p.length > 0);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onNavigate: () => void;
}

export function AppSidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onNavigate,
}: AppSidebarProps) {
  const { data: session } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  function isActive(item: NavItem): boolean {
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
    return (item.alsoMatches ?? []).some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  function renderItem(item: NavItem) {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <Link
        key={item.to}
        to={item.to}
        className="pa-nav"
        data-active={active}
        title={collapsed ? item.label : undefined}
        onClick={onNavigate}
      >
        <Icon aria-hidden="true" />
        {!collapsed && <span>{item.label}</span>}
        {!collapsed && item.phase !== undefined && (
          <span className="pa-nav-phase" title={`Phase ${item.phase.slice(1)} scope`}>
            {item.phase}
          </span>
        )}
      </Link>
    );
  }

  return (
    <nav className="pa-side" data-open={mobileOpen} aria-label="Main navigation">
      <div className="pa-side-brand">
        {!collapsed && (
          <>
            <img src={logoWhite} alt="Nymbl" />
            <span className="pa-side-brand-name">Audit</span>
          </>
        )}
        <button
          type="button"
          className="pa-side-toggle"
          onClick={onToggleCollapsed}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        >
          <PanelLeft aria-hidden="true" size={17} />
        </button>
      </div>

      {!collapsed && <div className="pa-side-section">Audit</div>}
      {AUDIT_NAV.map(renderItem)}

      {!collapsed && <div className="pa-side-section">Standard</div>}
      {collapsed && <div className="mx-1.5 my-3 h-px bg-white/10" />}
      {STANDARD_NAV.map(renderItem)}

      <Link to="/account" className="pa-side-footer" onClick={onNavigate} title="Account">
        <span className="pa-avatar" aria-hidden="true">
          {initials(session?.user.name, session?.user.email)}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="pa-user block truncate">
                {session?.user.name ?? session?.user.email ?? "Signed in"}
              </span>
              <span className="pa-user-role block truncate">
                {session?.user.role === "admin" ? "Power User" : "Auditor"} · Microsoft SSO
              </span>
            </span>
            <Settings aria-hidden="true" size={15} className="shrink-0 text-white/50" />
          </>
        )}
      </Link>
    </nav>
  );
}
